import { useCallback, useEffect, useId, useState, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import Dropdown from "../Dropdown.tsx";
import { getResponsiveValue } from "../Dropdown.tsx";
import DropdownLinks from "../DropdownLinks.tsx";
import { ContextFilter } from "../ContextFilter.tsx";
import useDropdownOptions from "../../hooks/useDropdownOptions.tsx";
import useIsMobileViewport from "../../hooks/useIsMobileViewport.ts";
import { helpers } from "../../util/helpers.ts";
import { useLocalPartial } from "../../util/local.ts";
import {
  getMobileBackControl,
  getMobileBackStackServerSnapshot,
  getMobileBackStackSnapshot,
  subscribeMobileBackStack,
} from "../../util/mobileBackStack.ts";
import { useViewData } from "../../util/viewManager.tsx";
import { realtimeUpdate } from "../../util/realtimeUpdate.ts";
import { translateMenuLinks } from "../../util/translateMenuLinks.ts";
import i18n from "../../util/i18n.ts";
import type { LocalStateUI, MenuItemHeader, MenuItemLink } from "../../../../../common/types.ts";

const genPath = (parts: string[], season: string | undefined) => {
  if (season !== undefined) {
    return [...parts, season];
  }

  return parts;
};

export const getContextFieldLabel = (field: string) => {
  if (field.startsWith("teams")) {
    return i18n.t("titleBar.fieldTeam", { ns: "ui", defaultValue: "Team" });
  }

  if (field.startsWith("seasons")) {
    return i18n.t("titleBar.fieldSeason", { ns: "ui", defaultValue: "Season" });
  }

  if (field === "days") {
    return i18n.t("titleBar.fieldDay", { ns: "ui", defaultValue: "Day" });
  }

  if (
    field === "statType" ||
    field === "statTypes" ||
    field === "statTypesAdv" ||
    field === "statTypesStrict" ||
    field.startsWith("type")
  ) {
    return i18n.t("titleBar.fieldStatType", { ns: "ui", defaultValue: "Stat" });
  }

  if (field === "playoffs" || field === "playoffsCombined") {
    return i18n.t("titleBar.fieldSplit", { ns: "ui", defaultValue: "Split" });
  }

  if (field === "teamOpponent" || field === "teamOpponentAdvanced" || field === "teamAdvanced") {
    return i18n.t("titleBar.fieldView", { ns: "ui", defaultValue: "View" });
  }

  if (field === "standingsType") {
    return i18n.t("titleBar.fieldStandingsType", { ns: "ui", defaultValue: "Standings" });
  }

  if (field === "cids" || field === "shows" || field === "newsLevels") {
    return i18n.t("titleBar.fieldScope", { ns: "ui", defaultValue: "Scope" });
  }

  return field;
};

const getContextResetLabel = (field: string) => {
  if (field.startsWith("seasons") || field.startsWith("teams")) {
    return i18n.t("titleBar.returnCurrent", {
      ns: "ui",
      defaultValue: "Current",
    });
  }

  return i18n.t("titleBar.clearFilter", {
    ns: "ui",
    defaultValue: "Clear",
  });
};

// Accessible name for a scope chip that doubles as its field's picker trigger,
// e.g. "Switch team, current: Hawks". Keep the placeholders identical in en/zh.
const getContextChipAriaLabel = (fieldLabel: string, valueLabel: string) =>
  i18n.t("titleBar.switchFieldAria", {
    ns: "ui",
    defaultValue: "Switch {{field}}, current: {{value}}",
    field: fieldLabel,
    value: valueLabel,
  });

const getContextCurrentValue = ({
  field,
  options,
  season,
  teamInfoCache,
  userTid,
}: {
  field: string;
  options: ReturnType<typeof useDropdownOptions>;
  season?: number;
  teamInfoCache?: LocalStateUI["teamInfoCache"];
  userTid?: number;
}) => {
  if (options.length === 0) {
    return;
  }

  if (field.startsWith("seasons")) {
    return options.find((option) => option.key === season)?.key;
  }

  if (field.startsWith("teams")) {
    const userTeamAbbrev =
      userTid !== undefined && userTid >= 0 ? teamInfoCache?.[userTid]?.abbrev : undefined;

    return options.find((option) => option.key === userTeamAbbrev || option.key === userTid)?.key;
  }

  if (
    field === "standingsType" ||
    field === "statType" ||
    field === "statTypes" ||
    field === "statTypesAdv" ||
    field === "statTypesStrict" ||
    field === "playoffs" ||
    field === "playoffsCombined" ||
    field === "teamOpponent" ||
    field === "teamOpponentAdvanced" ||
    field === "teamAdvanced" ||
    field === "cids" ||
    field === "shows" ||
    field === "newsLevels" ||
    field.startsWith("type")
  ) {
    return options[0]?.key;
  }
};

// A scope chip shows the field's current value AND is the tap target that opens
// that field's picker directly (no combined "Adjust scope" step). When the field
// has only one option there is nothing to switch to, so it renders as a static
// label instead of a button.
const MobileContextSummaryChip = ({
  customOptions,
  field,
  isOpen,
  onOpen,
  onReset,
  season,
  teamInfoCache,
  userTid,
  value,
}: {
  customOptions?: NonNullable<LocalStateUI["dropdownCustomOptions"]>[string];
  field: string;
  isOpen: boolean;
  onOpen: (field: string) => void;
  onReset: (value: number | string) => void;
  season?: number;
  teamInfoCache?: LocalStateUI["teamInfoCache"];
  userTid?: number;
  value: number | string;
}) => {
  const options = useDropdownOptions(field, customOptions);
  const selectedOption = options.find((option) => option.key === value);
  const selectedLabel =
    selectedOption !== undefined
      ? getResponsiveValue(selectedOption.value, Infinity)
      : String(value);
  const fieldLabel = getContextFieldLabel(field);
  const currentValue = getContextCurrentValue({
    field,
    options,
    season,
    teamInfoCache,
    userTid,
  });
  const canReset = currentValue !== undefined && currentValue !== value;
  const canOpen = options.length > 1;

  const chipContent = (
    <>
      <span className="mobile-context-chip-label">{fieldLabel}</span>
      <span className="mobile-context-chip-value">{selectedLabel}</span>
    </>
  );

  return (
    <span
      className="mobile-context-chip"
      data-mobile-context-chip=""
      data-mobile-context-current={canReset ? "false" : "true"}
      data-mobile-context-field={field}
      data-mobile-context-option-count={options.length}
    >
      {canOpen ? (
        <button
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label={getContextChipAriaLabel(fieldLabel, selectedLabel)}
          className="mobile-context-chip-trigger"
          data-mobile-context-chip-trigger=""
          data-p0-1-page-context-trigger=""
          data-v5-title-scope-chip={field}
          onClick={() => onOpen(field)}
          type="button"
        >
          {chipContent}
        </button>
      ) : (
        <span className="mobile-context-chip-static" data-v5-title-scope-chip={field}>
          {chipContent}
        </span>
      )}
      {canReset ? (
        <button
          className="mobile-context-chip-action"
          data-mobile-context-current-action=""
          onClick={(event) => {
            event.stopPropagation();
            onReset(currentValue);
          }}
          type="button"
        >
          {getContextResetLabel(field)}
        </button>
      ) : null}
    </span>
  );
};

const MobileContextField = ({
  customOptions,
  field,
  hideHeading = false,
  onChange,
  onClose,
  season,
  teamInfoCache,
  userTid,
  value,
}: {
  customOptions?: NonNullable<LocalStateUI["dropdownCustomOptions"]>[string];
  field: string;
  hideHeading?: boolean;
  onChange: (value: number | string) => void;
  onClose: () => void;
  season?: number;
  teamInfoCache?: LocalStateUI["teamInfoCache"];
  userTid?: number;
  value: number | string;
}) => {
  const options = useDropdownOptions(field, customOptions);

  if (options.length <= 1) {
    return null;
  }

  const selectedOption = options.find((option) => option.key === value);
  const selectedLabel =
    selectedOption !== undefined
      ? getResponsiveValue(selectedOption.value, Infinity)
      : String(value);
  const currentValue = getContextCurrentValue({
    field,
    options,
    season,
    teamInfoCache,
    userTid,
  });
  const canReset = currentValue !== undefined && currentValue !== value;

  return (
    <section className="mobile-context-section" data-mobile-context-field={field}>
      <div className="mobile-context-section-header">
        {hideHeading ? null : <h2>{getContextFieldLabel(field)}</h2>}
        <div className="mobile-context-section-current">
          <span>{selectedLabel}</span>
          {canReset ? (
            <button
              className="btn btn-link mobile-context-current"
              data-mobile-context-current-field={field}
              onClick={() => {
                onChange(currentValue);
                onClose();
              }}
              type="button"
            >
              {getContextResetLabel(field)}
            </button>
          ) : null}
        </div>
      </div>
      <div className="mobile-context-option-grid">
        {options.map((option) => {
          const active = option.key === value;
          const label = getResponsiveValue(option.value, Infinity);

          return (
            <button
              aria-pressed={active}
              className={`mobile-context-option btn ${
                active ? "btn-primary" : "btn-light-bordered"
              }`}
              data-mobile-context-option=""
              key={option.key}
              onClick={() => {
                onChange(option.key);
                onClose();
              }}
              type="button"
            >
              {label}
            </button>
          );
        })}
      </div>
    </section>
  );
};

const useContextSheetEscape = ({ close, open }: { close: () => void; open: boolean }) => {
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [close, open]);
};

const MobileTitleContextDialog = ({
  bodyData,
  children,
  close,
  closeAriaLabel,
  closeLabel,
  dialogID,
  title,
}: {
  bodyData: Record<`data-${string}`, string>;
  children: ReactNode;
  close: () => void;
  closeAriaLabel: string;
  closeLabel: string;
  dialogID: string;
  title: ReactNode;
}) => (
  <>
    <div
      className="modal-backdrop show mobile-title-context-modal-backdrop"
      data-mobile-context-dialog-backdrop=""
      onClick={close}
    />
    <div
      aria-labelledby={`${dialogID}-title`}
      aria-modal="true"
      className="modal show mobile-title-context-modal"
      data-mobile-context-dialog=""
      role="dialog"
      tabIndex={-1}
    >
      <div
        className="modal-dialog modal-dialog-centered mobile-title-context-modal-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-content mobile-title-context-modal-content">
          <div className="mobile-context-sheet-header mobile-context-dialog-header">
            <h2 id={`${dialogID}-title`}>{title}</h2>
            <button
              aria-label={closeAriaLabel}
              className="btn btn-light-bordered mobile-context-close"
              onClick={close}
              type="button"
            >
              {closeLabel}
            </button>
          </div>
          <div className="mobile-context-sheet-body mobile-context-dialog-body" {...bodyData}>
            {children}
          </div>
        </div>
      </div>
    </div>
  </>
);

export const PageContextSummary = ({
  customOptions,
  fields,
  onOpen,
  onReset,
  openField,
  season,
  teamInfoCache,
  userTid,
}: {
  customOptions?: LocalStateUI["dropdownCustomOptions"];
  fields?: Record<string, number | string>;
  onOpen: (field: string) => void;
  onReset: (field: string, value: number | string) => void;
  openField?: string | null;
  season?: number;
  teamInfoCache?: LocalStateUI["teamInfoCache"];
  userTid?: number;
}) => {
  const keys = fields !== undefined ? Object.keys(fields) : [];

  if (keys.length === 0) {
    return null;
  }

  return (
    <div className="mobile-context-summary text-body-secondary" data-mobile-context-summary="">
      {keys.map((key) => (
        <span className="mobile-context-summary-item" key={key}>
          <MobileContextSummaryChip
            customOptions={customOptions?.[key]}
            field={key}
            isOpen={openField === key}
            onOpen={onOpen}
            onReset={(value) => onReset(key, value)}
            season={season}
            teamInfoCache={teamInfoCache}
            userTid={userTid}
            value={fields![key]!}
          />
        </span>
      ))}
    </div>
  );
};

// Direct-manipulation scope: the chips ARE the triggers. Each chip opens a
// picker scoped to its own field (reusing the centered MobileTitleContextDialog),
// so there is no combined "Adjust scope" step. Selecting a value flows through
// onChange, producing the same navigation the combined picker used to.
export const PageContextSheet = ({
  customOptions,
  fields,
  initialOpen = false,
  initialOpenField,
  onChange,
  season,
  teamInfoCache,
  userTid,
}: {
  customOptions?: LocalStateUI["dropdownCustomOptions"];
  fields?: Record<string, number | string>;
  initialOpen?: boolean;
  initialOpenField?: string;
  onChange: (field: string, value: number | string) => void;
  season?: number;
  teamInfoCache?: LocalStateUI["teamInfoCache"];
  userTid?: number;
}) => {
  const keys = fields !== undefined ? Object.keys(fields) : [];
  const [openField, setOpenField] = useState<string | null>(() => {
    if (initialOpenField !== undefined && keys.includes(initialOpenField)) {
      return initialOpenField;
    }

    return initialOpen && keys.length > 0 ? keys[0]! : null;
  });
  const sheetID = useId();
  const close = useCallback(() => {
    setOpenField(null);
  }, []);

  useContextSheetEscape({ close, open: openField !== null });

  if (fields === undefined || keys.length === 0) {
    return null;
  }

  const activeField = openField !== null && keys.includes(openField) ? openField : null;

  return (
    <>
      <PageContextSummary
        customOptions={customOptions}
        fields={fields}
        onOpen={setOpenField}
        onReset={onChange}
        openField={activeField}
        season={season}
        teamInfoCache={teamInfoCache}
        userTid={userTid}
      />
      {activeField !== null ? (
        <MobileTitleContextDialog
          bodyData={{
            "data-mobile-context-sheet": "",
          }}
          close={close}
          closeAriaLabel={i18n.t("titleBar.closeContext", {
            ns: "ui",
            defaultValue: "Close scope",
          })}
          closeLabel={i18n.t("titleBar.closeContextShort", {
            ns: "ui",
            defaultValue: "Close",
          })}
          dialogID={sheetID}
          title={getContextFieldLabel(activeField)}
        >
          <MobileContextField
            customOptions={customOptions?.[activeField]}
            field={activeField}
            hideHeading
            key={activeField}
            onChange={(value) => onChange(activeField, value)}
            onClose={close}
            season={season}
            teamInfoCache={teamInfoCache}
            userTid={userTid}
            value={fields[activeField]!}
          />
        </MobileTitleContextDialog>
      ) : null}
    </>
  );
};

// R3-2: the mobile RelatedPagesMenu (the "更多" / related-pages "Jump To" menu,
// data-p0-1-related-pages-trigger) is removed. 2026-07-14 审核意见 3 judged the
// hard-coded jump-to list (Standings/Playoffs/Season Summary, including a
// self-link to the current page) redundant on mobile — bottom-tab navigation and
// in-page links already reach those pages. This is a deliberate reversal of the
// earlier P0R behavior (which had relocated the menu into a centered dialog); the
// component and its MobileContextMenuItem helper are deleted, and the mobile title
// context now renders only the per-field scope chips (PageContextSheet). The
// desktop DropdownLinks "Jump To" path is unchanged — see the TitleBar body, which
// still builds translatedDropdownItems for DropdownLinks.

export const MobileTitleContext = ({
  customOptions,
  customURL,
  fields,
  initialOpen = false,
  initialOpenField,
  season,
  teamInfoCache,
  userTid,
  view,
}: {
  customOptions?: LocalStateUI["dropdownCustomOptions"];
  customURL?: (fields: Record<string, number | string>) => string;
  fields?: Record<string, number | string>;
  initialOpen?: boolean;
  initialOpenField?: string;
  season?: number;
  teamInfoCache?: LocalStateUI["teamInfoCache"];
  userTid?: number;
  view?: string;
}) => {
  const keys = fields !== undefined ? Object.keys(fields) : [];

  const handleFieldChange = useCallback(
    (field: string, value: number | string) => {
      if (fields === undefined || view === undefined) {
        return;
      }

      const newFields = {
        ...fields,
        [field]: value,
      };
      const url =
        customURL !== undefined
          ? customURL(newFields)
          : helpers.leagueUrl([view, ...keys.map((key) => newFields[key]!)]);

      realtimeUpdate([], url);
    },
    [customURL, fields, keys, view],
  );

  if (keys.length === 0) {
    return null;
  }

  return (
    <div className="ms-auto mobile-title-context" data-mobile-title-context="">
      <PageContextSheet
        customOptions={customOptions}
        fields={fields}
        initialOpen={initialOpen}
        initialOpenField={initialOpenField}
        onChange={handleFieldChange}
        season={season}
        teamInfoCache={teamInfoCache}
        userTid={userTid}
      />
    </div>
  );
};

const MobileTitleBackControl = () => {
  const viewInfo = useViewData();
  const { phase, playMenuOptions } = useLocalPartial(["phase", "playMenuOptions"]);
  // Subscribe so the control re-renders whenever the back stack changes.
  useSyncExternalStore(
    subscribeMobileBackStack,
    getMobileBackStackSnapshot,
    getMobileBackStackServerSnapshot,
  );

  if (!viewInfo?.inLeague) {
    return null;
  }

  const pageID: unknown = viewInfo.idLoading ?? viewInfo.idLoaded;
  const backControl = getMobileBackControl({
    hasPendingNegotiations: playMenuOptions.some(
      (option) => option.id === "contractNegotiationList",
    ),
    needsExpansionProtection: playMenuOptions.some((option) => option.id === "expansionDraft"),
    pageID,
    phase,
    playerTid:
      pageID === "player" && typeof viewInfo.data?.player?.tid === "number"
        ? viewInfo.data.player.tid
        : undefined,
  });

  if (!backControl) {
    return null;
  }

  return (
    <a
      aria-label={i18n.t("titleBar.back", { ns: "ui", defaultValue: "Back" })}
      className="btn btn-link btn-sm title-bar-back px-0 me-2 d-inline-flex align-items-center"
      data-mobile-title-back=""
      href={backControl.href}
    >
      <svg
        aria-hidden="true"
        className="title-bar-back-icon"
        fill="none"
        height="18"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        width="18"
      >
        <path d="M15 5l-7 7 7 7" />
      </svg>
      <span className="title-bar-back-label">{backControl.label}</span>
    </a>
  );
};

export const TitleBar = ({ chromeDensity }: { chromeDensity: "default" | "compact" }) => {
  const {
    title,
    customMenu,
    jumpTo,
    jumpToSeason,
    dropdownCustomOptions,
    dropdownCustomURL,
    dropdownView,
    dropdownFields,
    lid,
    season,
    teamInfoCache,
    userTid,
  } = useLocalPartial([
    "title",
    "customMenu",
    "jumpTo",
    "jumpToSeason",
    "dropdownCustomOptions",
    "dropdownCustomURL",
    "dropdownView",
    "dropdownFields",
    "lid",
    "season",
    "teamInfoCache",
    "userTid",
  ]);
  const isMobile = useIsMobileViewport();

  if (title === undefined) {
    return null;
  }

  const dropdownItems: (MenuItemHeader | MenuItemLink)[] = [];

  if (jumpTo) {
    // Sometimes the season will be some nonsense like "all", in which case we can't generally use
    // it (although maybe it would be good to in some cases).
    const season = typeof jumpToSeason === "number" ? String(jumpToSeason) : undefined;
    dropdownItems.push({
      type: "header",
      long: "Jump To",
      short: "Jump To",
      league: true,
      children: [
        {
          type: "link",
          league: true,
          path: genPath(["standings"], season),
          text: "Standings",
        },
        {
          type: "link",
          league: true,
          path: genPath(["playoffs"], season),
          text: "Playoffs",
        },
        {
          type: "link",
          league: true,
          path: genPath(["history"], season),
          text: "Season Summary",
        },
      ],
    });
  }

  if (customMenu) {
    dropdownItems.push(customMenu);
  }

  const translatedDropdownItems = translateMenuLinks(dropdownItems);

  return (
    <aside
      className={`navbar navbar-border navbar-light justify-content-start title-bar flex-shrink-0 py-0 ${
        chromeDensity === "compact" ? "title-bar-compact-chrome" : ""
      }`}
      data-mobile-context-header=""
    >
      {isMobile ? <MobileTitleBackControl /> : null}
      <h1 className="title-bar-heading">{title}</h1>
      {isMobile ? (
        <MobileTitleContext
          customOptions={dropdownCustomOptions}
          customURL={dropdownCustomURL}
          fields={dropdownFields}
          season={season}
          teamInfoCache={teamInfoCache}
          userTid={userTid}
          view={dropdownView}
        />
      ) : (
        <>
          {dropdownView && dropdownFields ? (
            <ContextFilter>
              <Dropdown
                customURL={dropdownCustomURL}
                customOptions={dropdownCustomOptions}
                view={dropdownView}
                fields={dropdownFields}
              />
            </ContextFilter>
          ) : null}
          <DropdownLinks
            className="ms-auto title-bar-right-links"
            hideTitle
            inLeague
            items={translatedDropdownItems}
            lid={lid}
          />
        </>
      )}
    </aside>
  );
};
